import React, { useEffect, useState } from 'react';
import { aiAgentApi } from '../../../lib/aiAgentApi';
import { toast } from 'sonner';
import { MoreVertical, Pencil, Trash2, Plus, MessageSquare, Users } from 'lucide-react';

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  } catch { return iso; }
};

const ThreadItem = ({ conv, onOpen, onRefresh }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newTitle, setNewTitle] = useState(conv.title || '');

  const isShared = (conv.collaborator_user_ids?.length || 0) > 0 || conv.role === 'collaborator';
  const titleText = (conv.title && conv.title.trim()) || 'Untitled chat';

  const doDelete = async () => {
    if (!window.confirm('Delete this chat permanently?')) return;
    try {
      await aiAgentApi.deleteConversation(conv.id);
      toast.success('Chat deleted');
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete');
    }
    setMenuOpen(false);
  };

  const doRename = async () => {
    const t = (newTitle || '').trim();
    if (!t) { setRenaming(false); return; }
    try {
      await aiAgentApi.renameConversation(conv.id, t);
      toast.success('Renamed');
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not rename');
    }
    setRenaming(false);
    setMenuOpen(false);
  };

  return (
    <li
      data-testid={`ai-thread-row-${conv.id}`}
      className="group relative cursor-pointer rounded-xl border border-slate-200 bg-white px-4 py-3 transition hover:border-emerald-200 hover:shadow-sm"
      onClick={() => !menuOpen && !renaming && onOpen(conv)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {renaming ? (
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <input
                data-testid={`ai-thread-rename-input-${conv.id}`}
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') doRename();
                  if (e.key === 'Escape') setRenaming(false);
                }}
                autoFocus
                className="w-full rounded-md border border-emerald-300 px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-emerald-100"
              />
              <button onClick={doRename} className="rounded-md bg-emerald-600 px-2 py-1 text-xs font-semibold text-white">Save</button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="truncate text-sm font-semibold text-slate-900">{titleText}</div>
              {isShared && (
                <span title="Shared with teammates" className="inline-flex items-center gap-0.5 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">
                  <Users className="h-3 w-3" /> {conv.role === 'collaborator' ? 'Joined' : (conv.collaborator_user_ids?.length || 0)}
                </span>
              )}
            </div>
          )}
          <div className="mt-0.5 truncate text-xs text-slate-500">
            {conv.last_snippet
              ? <span><span className="font-medium text-slate-400">{conv.last_snippet_role === 'assistant' ? 'Agent: ' : 'You: '}</span>{conv.last_snippet}</span>
              : <span className="italic text-slate-400">No messages yet</span>}
          </div>
        </div>
        <div className="flex items-start gap-2">
          <div className="whitespace-nowrap text-[11px] text-slate-400">{fmtDate(conv.last_active_at || conv.started_at)}</div>
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <button
              data-testid={`ai-thread-menu-${conv.id}`}
              onClick={() => setMenuOpen((v) => !v)}
              className="rounded-md p-1 text-slate-400 opacity-0 group-hover:opacity-100 hover:bg-slate-100 hover:text-slate-900"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-7 z-20 w-36 rounded-lg border border-slate-200 bg-white shadow-xl">
                <button
                  data-testid={`ai-thread-delete-${conv.id}`}
                  onClick={doDelete}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-rose-600 hover:bg-rose-50"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
                <button
                  data-testid={`ai-thread-rename-${conv.id}`}
                  onClick={() => { setRenaming(true); setMenuOpen(false); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit Name
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </li>
  );
};

export const ChatList = ({ refreshKey, onOpenThread, onNewChat }) => {
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await aiAgentApi.listConversations(100);
      setConvs(r.conversations || []);
    } catch {
      toast.error('Could not load chats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [refreshKey]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <div className="text-sm font-semibold text-slate-900">Chats</div>
        <button
          data-testid="ai-new-chat-btn"
          onClick={onNewChat}
          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
        >
          <Plus className="h-3.5 w-3.5" /> New chat
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3">
        {loading && <div className="text-center text-xs text-slate-400 py-8">Loading…</div>}
        {!loading && convs.length === 0 && (
          <div className="mt-12 flex flex-col items-center justify-center px-4 text-center">
            <div className="rounded-full bg-emerald-50 p-3 text-emerald-600 ring-1 ring-emerald-100">
              <MessageSquare className="h-6 w-6" />
            </div>
            <div className="mt-3 text-sm font-semibold text-slate-900">No chats yet</div>
            <div className="mt-1 max-w-xs text-xs text-slate-500">
              Start a conversation to ask the agent about your bills, payment files, vendors, or to draft a vendor email.
            </div>
            <button
              data-testid="ai-empty-new-chat"
              onClick={onNewChat}
              className="mt-4 rounded-lg bg-emerald-600 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-700"
            >
              Start a new chat
            </button>
          </div>
        )}
        <ul className="space-y-2">
          {convs.map((c) => (
            <ThreadItem key={c.id} conv={c} onOpen={onOpenThread} onRefresh={load} />
          ))}
        </ul>
      </div>
    </div>
  );
};
